import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_PRODUCT, DELETE_PRODUCT } from '~/services/store';
import { useForm } from 'react-hook-form';
import { Field } from '~/components/shared/Field';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { ReactComponent as NoImageIcon } from '~/assets/svg/no_imagen.svg';

// Interfaces for the product
interface ProductVariant {
  id: string;
  title: string;
  price: string;
  currency: string;
  inventory: string;
  image?: File | null;
  imageURL?: string;
  selectedOptions?: SelectedOption[];
}

interface SelectedOption {
  name: string;
  value: string;
}

interface ProductFormData {
  title: string;
  description: string;
  price: string;
  currency: string;
  inventory: string;
  featuredImage?: File | null;
  videoUrl?: string;
  expirationDate?: string;
}

interface Product {
  id?: string;
  title: string;
  totalInventory: number;
  description: string;
  onlineStoreUrl?: string;
  onlineStorePreviewUrl?: string;
  mainImageURL?: string;
  priceRangeV2: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice?: {
      amount: string;
      currencyCode: string;
    };
  };
  variants?: any[];
}

const ProductEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [t] = useTranslation('store');
  const { agentId } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [mainImage, setMainImage] = useState<File | null>(null);
  const [variantImagePreviews, setVariantImagePreviews] = useState<{
    [key: string]: string;
  }>({});
  const [showToastAlert, setShowToastAlert] = useState(false);
  const [showToastSuccess, setShowToastSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ProductFormData>({
    defaultValues: {
      currency: 'COP'
    }
  });

  const [getProduct, { loading, error }] = useLazyQuery(GET_PRODUCT, {
    variables: { data: { agent_id: agentId, product_id: id } },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const productData = data.get_product.data.product;
      setProduct(productData);

      // Set form values
      setValue('title', productData.title);
      setValue('description', productData.description);
      setValue('inventory', productData.totalInventory.toString());
      setValue('price', productData.priceRangeV2.minVariantPrice.amount);
      setValue('currency', productData.priceRangeV2.minVariantPrice.currencyCode || 'COP');
      
      // Set videoUrl if available
      if (productData.videoUrl) {
        setValue('videoUrl', productData.videoUrl);
      }

      // Set image preview if available
      if (productData.mainImageURL) {
        setImagePreview(productData.mainImageURL);
      }

      // Set variants if available
      if (productData.variants && productData.variants.length > 0) {
        // Filter out the main variant (variant_main) and only keep additional variants
        const additionalVariants = productData.variants.filter((variant: any) => 
          !variant.id.includes('variant_main')
        );

        const mappedVariants = additionalVariants.map((variant: any) => ({
          id:
            variant.id ||
            `variant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          title: variant.title || '',
          price: variant.price?.toString() || variant.priceV2?.amount || '0',
          currency: variant.priceV2?.currencyCode || variant.currencyCode || 'COP',
          inventory:
            variant.inventoryQuantity?.toString() ||
            variant.inventory?.toString() ||
            '0',
          imageURL: variant.image?.url || variant.imageURL || '',
          selectedOptions: variant.selectedOptions || [],
        }));

        console.log('Mapped variants from server (excluding main):', mappedVariants);
        setVariants(mappedVariants);

        // Set variant image previews
        const previews: { [key: string]: string } = {};
        mappedVariants.forEach((variant: ProductVariant) => {
          if (variant.imageURL) {
            previews[variant.id] = variant.imageURL;
          }
        });
        setVariantImagePreviews(previews);
      }
    },
  });

  const [deleteProduct] = useMutation(DELETE_PRODUCT, {
    onCompleted: (data) => {
      if (!data.delete_product.hasError) {
        showToastWarning('Producto eliminado exitosamente', false);
        setTimeout(() => {
          navigate(`/agent/${agentId}/store`);
        }, 1500);
      } else {
        showToastWarning(
          data.delete_product.message || 'Error al eliminar el producto',
          true,
        );
      }
      setShowDeleteDialog(false);
    },
    onError: (error) => {
      console.error('Error deleting product:', error);
      showToastWarning('Error al eliminar el producto', true);
      setShowDeleteDialog(false);
    },
  });

  useEffect(() => {
    if (id) {
      getProduct();
    }
  }, [id, getProduct]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setImagePreview(e.target.result as string);
        setMainImage(file);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleVariantImageUpload = (variantId: string, file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setVariantImagePreviews((prev) => ({
          ...prev,
          [variantId]: e.target!.result as string,
        }));
        updateVariant(variantId, 'image', file);
      }
    };
    reader.readAsDataURL(file);
  };

  const addVariant = () => {
    const newVariant: ProductVariant = {
      id: `variant-${Date.now()}`,
      title: '',
      price: '',
      currency: 'COP', // Se mantendrá para compatibilidad pero usará la moneda principal
      inventory: '0',
      image: null,
    };
    setVariants([...variants, newVariant]);
  };

  const removeVariant = (variantId: string) => {
    setVariants(variants.filter((variant) => variant.id !== variantId));
    // Remove the image preview for this variant
    setVariantImagePreviews((prev) => {
      const newPreviews = { ...prev };
      delete newPreviews[variantId];
      return newPreviews;
    });
  };

  const updateVariant = (
    variantId: string,
    field: keyof ProductVariant,
    value: string | File | null,
  ) => {
    setVariants(
      variants.map((variant) =>
        variant.id === variantId ? { ...variant, [field]: value } : variant,
      ),
    );
  };

  const showToastWarning = (mensaje: string, isWarning: boolean) => {
    if (isWarning) setShowToastAlert(true);
    else setShowToastSuccess(true);
    setMessage(mensaje);
    setTimeout(() => {
      if (isWarning) setShowToastAlert(false);
      else setShowToastSuccess(false);
    }, 3000);
  };

  const handleDeleteProduct = () => {
    if (!agentId || !id) {
      showToastWarning('ID del agente o producto no disponible', true);
      return;
    }

    deleteProduct({
      variables: {
        data: {
          agent_id: agentId,
          product_id: id,
        },
      },
    });
  };

  const onSubmit = async (data: ProductFormData) => {
    try {
      // Validación básica
      if (!agentId) {
        showToastWarning('ID del agente no disponible', true);
        return;
      }

      // Validar que las variantes adicionales estén completas si existen
      if (variants.length > 0) {
        const incompleteVariants = variants.filter(
          (variant) =>
            !variant.title.trim() || !variant.price || !variant.inventory,
        );

        if (incompleteVariants.length > 0) {
          showToastWarning(
            'Por favor completa todos los campos de las variantes antes de guardar',
            true,
          );
          return;
        }
      }

      // Crear FormData para enviar archivos
      const formData = new FormData();

      // Agregar datos básicos del producto
      formData.append('agent_id', agentId);
      formData.append('product_id', id || '');
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('price', data.price);
      formData.append('currency', data.currency);
      formData.append('inventory', data.inventory);

      if (data.videoUrl) {
        formData.append('videoUrl', data.videoUrl);
      }

      if (data.expirationDate) {
        formData.append('expirationDate', data.expirationDate);
      }

      // Array para llevar registro del orden de archivos y cuáles fueron modificadas
      const imageUpload: number[] = [];

      // Determinar qué imágenes fueron modificadas y crear el array de índices
      if (mainImage) {
        imageUpload.push(0); // Índice 0 para imagen principal (variante principal)
      }

      variants.forEach((variant, index) => {
        if (variant.image) {
          imageUpload.push(index + 1); // Índices 1, 2, 3... para variantes adicionales
        }
      });

      // Agregar archivos en el orden correcto según los índices
      // Agregar imagen principal si existe (irá a variante principal)
      if (mainImage) {
        formData.append('files', mainImage);
      }

      // Agregar imágenes de variantes adicionales en orden
      variants.forEach((variant) => {
        if (variant.image) {
          formData.append('files', variant.image);
        }
      });

      // Enviar array de índices de imágenes modificadas
      if (imageUpload.length > 0) {
        formData.append('imageUpload', JSON.stringify(imageUpload));
      }

      // Agregar variantes adicionales (la variante principal se actualiza automáticamente en el backend)
      if (variants.length > 0) {
        // Calcular el índice de cada variante en el array de archivos
        let imageIndex = mainImage ? 1 : 0; // Si hay imagen principal, las variantes empiezan en índice 1

        // Enviar todas las variantes, incluyendo las existentes y las nuevas
        const variantsToSend = variants.map((variant, variantIndex) => {
          const variantData = {
            id: variant.id,
            title: variant.title || '',
            price: variant.price || '0',
            currency: data.currency, // Usar la moneda principal del producto
            inventory: variant.inventory || '0',
            hasNewImage: !!variant.image, // Indicar si la variante tiene imagen nueva
            imageIndex: variant.image ? imageIndex : null, // Índice en el array de archivos
          };

          // Incrementar imageIndex solo si esta variante tiene imagen
          if (variant.image) {
            imageIndex++;
          }

          return variantData;
        });

        console.log('Variants to send:', variantsToSend);
        formData.append('variants', JSON.stringify(variantsToSend));
      } else {
        // Enviar array vacío si no hay variantes adicionales
        formData.append('variants', JSON.stringify([]));
      }

      // Realizar la petición a la API de actualización
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/products/updateProduct`,
        {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        },
      );

      const result = await response.json();

      if (response.ok && !result.hasError) {
        showToastWarning('Producto actualizado exitosamente', false);
        // Redirigir de vuelta al inventario después de un breve delay
        setTimeout(() => {
          navigate(`/agent/${agentId}/store`);
        }, 500);
      } else {
        showToastWarning(
          result.message || 'Error al actualizar el producto',
          true,
        );
      }
    } catch (error: any) {
      console.error('Error updating product:', error);
      showToastWarning(
        error.message || 'Error al actualizar el producto',
        true,
      );
    }
  };

  if (loading) {
    return (
      <div className="w-full animate-pulse space-y-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 rounded bg-gray-300"></div>
            <div className="h-4 w-32 rounded bg-gray-300"></div>
          </div>
        </div>

        {/* Main content skeleton */}
        <div className="overflow-hidden rounded-lg bg-white shadow-lg">
          <div className="grid grid-cols-1 gap-8 p-6 md:grid-cols-2">
            {/* Image skeleton */}
            <div className="space-y-4">
              <div className="aspect-square rounded-lg bg-gray-300"></div>
            </div>

            {/* Product info skeleton */}
            <div className="space-y-6">
              <div>
                <div className="mb-2 h-8 rounded bg-gray-300"></div>
                <div className="h-6 w-24 rounded bg-gray-300"></div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="mb-2 h-6 w-20 rounded bg-gray-300"></div>
                  <div className="space-y-2">
                    <div className="h-4 rounded bg-gray-300"></div>
                    <div className="h-4 rounded bg-gray-300"></div>
                    <div className="h-4 w-3/4 rounded bg-gray-300"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product && !loading) {
    return (
      <div className="w-full space-y-4">
        <button
          onClick={handleBackClick}
          className="mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          {t('back_to_inventory')}
        </button>
        <div className="py-12 text-center">
          <p className="text-gray-600">
            {error ? t('loading_error') : t('product_not_found')}
          </p>
          {error && (
            <p className="mt-2 text-sm text-red-500">{error.message}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header con botón de regresar y actualizar */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBackClick}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          {t('back_to_inventory')}
        </button>

        <button
          type="submit"
          form="product-edit-form"
          disabled={isSubmitting}
          className="rounded-lg bg-green-500 px-6 py-2 text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Guardando...' : 'Actualizar producto'}
        </button>
      </div>

      <form
        id="product-edit-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
      >
        {/* Contenido principal */}
        <div className="overflow-hidden rounded-lg bg-white shadow-lg">
          <div className="grid grid-cols-1 gap-8 p-6 md:grid-cols-2">
            {/* Imagen del producto */}
            <div className="form-control my-2 flex max-w-full flex-col gap-2">
              <div className="label flex items-center justify-between">
                <span className="label-text font-bold">
                  Imagen del producto
                </span>
              </div>
              <div className="mt-1">
                <div
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        handleImageUpload(file);
                      }
                    };
                    input.click();
                  }}
                  className="relative aspect-square cursor-pointer overflow-hidden rounded-lg border-2 border-dashed border-gray-300 bg-gray-100 transition-colors hover:border-gray-400"
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center p-4">
                      <NoImageIcon
                        title="No image"
                        className="mb-2 h-16 w-16 opacity-50"
                      />
                      <span className="text-sm text-gray-500">
                        {t('product.upload_image')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Información del producto */}
            <div className="space-y-6">
              <Field
                register={register}
                label={t('product.title') || 'Título del producto'}
                fieldName="title"
                watch={watch}
                placeholder="Ingresa el título del producto"
                options={{ required: 'Este campo es requerido' }}
                textError={errors.title?.message}
              />

              {/* Precio y Moneda en la misma línea */}
              <div className="grid grid-cols-2 gap-3">
                {/* Campo Precio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('product.price') || 'Precio'}
                  </label>
                  <input
                    type="number"
                    {...register('price', { required: 'Este campo es requerido', min: { value: 0, message: 'El precio debe ser mayor a 0' } })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {errors.price && (
                    <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                  )}
                </div>
                
                {/* Campo Moneda */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('product.currency') || 'Moneda'}
                  </label>
                  <select
                    {...register('currency', { required: 'Este campo es requerido' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="COP">{t('product.currencies.COP') || 'COP - Peso Colombiano'}</option>
                    <option value="USD">{t('product.currencies.USD') || 'USD - Dólar Americano'}</option>
                    <option value="EUR">{t('product.currencies.EUR') || 'EUR - Euro'}</option>
                    <option value="GBP">{t('product.currencies.GBP') || 'GBP - Libra Esterlina'}</option>
                    <option value="CNY">{t('product.currencies.CNY') || 'CNY - Yuan Chino'}</option>
                  </select>
                  {errors.currency && (
                    <p className="mt-1 text-sm text-red-600">{errors.currency.message}</p>
                  )}
                </div>
              </div>

              <Field
                register={register}
                label={t('product.inventory') || 'Inventario'}
                fieldName="inventory"
                type="number"
                watch={watch}
                placeholder="0"
                options={{
                  required: 'Este campo es requerido',
                  min: {
                    value: 0,
                    message: 'El inventario debe ser mayor o igual a 0',
                  },
                }}
                textError={errors.inventory?.message}
              />

              <Field
                register={register}
                label="URL del video (Opcional)"
                fieldName="videoUrl"
                watch={watch}
                placeholder="https://youtube.com/watch?v=..."
                tooltip="Enlace a un video promocional del producto (YouTube, Vimeo, etc.)"
              />

              <Field
                register={register}
                label="Fecha de expiración (Opcional)"
                fieldName="expirationDate"
                type="date"
                watch={watch}
                tooltip="Fecha hasta cuando estará disponible el producto"
              />
            </div>
          </div>

          {/* Descripción */}
          <div className="px-6 pb-6">
            <Field
              register={register}
              label={t('product.description') || 'Descripción'}
              fieldName="description"
              type="textarea"
              watch={watch}
              placeholder="Describe tu producto..."
              options={{ required: 'Este campo es requerido' }}
              textError={errors.description?.message}
              className="max-w-full"
            />
          </div>
        </div>

        {/* Variantes del producto */}
        <div className="rounded-lg bg-white p-6 shadow-lg">
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900">
              {t('product_variants') || 'Variantes del producto'}
            </h3>
            <button
              type="button"
              onClick={addVariant}
              className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
            >
              <PlusIcon className="h-5 w-5" />
              Agregar variante
            </button>
          </div>

          {variants.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <p>
                No hay variantes agregadas. Haz clic en "Agregar variante" para
                empezar.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {variants.map((variant, index) => (
                <div key={variant.id} className="rounded-lg border p-4">
                  <div className="mb-4 flex items-center justify-between">
                    <h4 className="font-medium">Variante {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeVariant(variant.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Título
                      </label>
                      <input
                        type="text"
                        value={variant.title}
                        onChange={(e) =>
                          updateVariant(variant.id, 'title', e.target.value)
                        }
                        placeholder="Título de la variante"
                        className="w-full rounded-md border p-2"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Precio
                      </label>
                      <input
                        type="number"
                        value={variant.price}
                        onChange={(e) =>
                          updateVariant(variant.id, 'price', e.target.value)
                        }
                        placeholder="0.00"
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full rounded-md border p-2"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Inventario
                      </label>
                      <input
                        type="number"
                        value={variant.inventory}
                        onChange={(e) =>
                          updateVariant(variant.id, 'inventory', e.target.value)
                        }
                        placeholder="0"
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full rounded-md border p-2"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Imagen (Opcional)
                    </label>
                    <div className="relative flex h-24 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200">
                      <input
                        type="file"
                        accept="image/*"
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        onChange={(e) =>
                          handleVariantImageUpload(
                            variant.id,
                            e.target.files![0],
                          )
                        }
                      />
                      {variantImagePreviews[variant.id] ? (
                        <img
                          src={variantImagePreviews[variant.id]}
                          alt="Variant preview"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <NoImageIcon
                          className="h-16 w-16 opacity-50"
                          title="No variant image"
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setShowDeleteDialog(true)}
            className="rounded-lg bg-red-500 px-4 py-2 text-white transition-colors hover:bg-red-600"
          >
            Eliminar producto
          </button>

          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg bg-green-500 px-6 py-2 text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Guardando...' : 'Actualizar producto'}
            </button>

            <button
              type="button"
              onClick={handleBackClick}
              className="rounded-lg border border-gray-300 px-6 py-2 text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      </form>

      {/* Toast notifications */}
      {showToastAlert && (
        <div className="toast-button toast toast-center w-80">
          <div className="alert alert-warning">
            <span>{message}</span>
          </div>
        </div>
      )}

      {showToastSuccess && (
        <div className="toast-button toast toast-center w-80">
          <div className="alert alert-success">
            <span>{message}</span>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación - DaisyUI Style */}
      <dialog
        id="delete_product_modal"
        className={`modal ${showDeleteDialog ? 'modal-open' : ''}`}
      >
        <div className="modal-box">
          <form method="dialog">
            <button
              className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
              onClick={() => setShowDeleteDialog(false)}
            >
              ✕
            </button>
          </form>

          <h3 className="text-lg font-bold">Confirmar eliminación</h3>
          <p className="py-4">
            ¿Estás seguro de que deseas eliminar este producto? Esta acción no
            se puede deshacer.
          </p>

          <div className="modal-action">
            <button
              type="button"
              onClick={() => setShowDeleteDialog(false)}
              className="btn btn-ghost"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleDeleteProduct}
              className="btn btn-error"
            >
              Eliminar
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowDeleteDialog(false)}>close</button>
        </form>
      </dialog>
    </div>
  );
};

export default ProductEdit;
